import { useState, useEffect, type FC } from 'react';
import { Link } from 'react-router-dom';
import { Save, Trash2, BarChart3, LineChart, PieChart, Table as TableIcon, Hash, Loader2, Pencil } from 'lucide-react';
import { Button } from './ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from './ui/breadcrumb';
import { EditableTitle } from './EditableTitle';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle as DialogTitleText,
} from './ui/dialog';
import { Input } from './ui/input';

const CHART_TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  TABLE: TableIcon,
  BAR: BarChart3,
  LINE: LineChart,
  PIE: PieChart,
  SINGLE_VALUE: Hash,
};

interface ChartTopBarProps {
  chartName?: string;
  chartDescription?: string;
  chartType?: string;
  chartData?: Record<string, any>[];
  chartConfig?: any;
  dateRange?: string;
  customDateStart?: string;
  customDateEnd?: string;
  onDateRangeChange?: (dateRange: string, customStart?: string, customEnd?: string) => void;
  hasUnsavedChanges?: boolean;
  onSave?: () => void;
  isSaving?: boolean;
  onRename?: (newName: string) => void | Promise<void>;
  onDescriptionChange?: (newDescription: string) => void;
  onDelete?: () => Promise<void>;
}

export const ChartTopBar: FC<ChartTopBarProps> = ({
  chartName = 'Untitled Report',
  chartDescription,
  chartType,
  hasUnsavedChanges = false,
  onSave,
  isSaving = false,
  onRename,
  onDescriptionChange,
  onDelete,
}) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDescriptionDialog, setShowDescriptionDialog] = useState(false);
  const [editedDescription, setEditedDescription] = useState(chartDescription || '');
  const chartTypeIcon = chartType ? (() => {
    const IconComponent = CHART_TYPE_ICONS[chartType] || BarChart3;
    return <IconComponent className="h-5 w-5 text-muted-foreground" />;
  })() : null;

  return (
    <div className="flex items-center justify-between w-full px-6 py-4 page-header-surface">
      {/* Left side: Breadcrumbs and Chart name */}
      <div className="flex flex-col gap-2 flex-1 min-w-0">
        {/* Breadcrumbs */}
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/analytics">Analytics</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{chartName}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        {/* Chart name */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {chartTypeIcon && (
            <div className="flex-shrink-0">
              {chartTypeIcon}
            </div>
          )}
          {onRename ? (
            <EditableTitle
              title={chartName}
              onSave={onRename}
              className="flex-1 min-w-0"
              titleClassName="truncate"
            />
          ) : (
            <h1 className="text-xl font-semibold text-foreground truncate">{chartName}</h1>
          )}
        </div>
        {onDescriptionChange ? (
          <div className="flex items-center gap-2 group">
            <p className="text-sm text-muted-foreground">{chartDescription || 'No description'}</p>
            <button
              onClick={() => {
                setEditedDescription(chartDescription || '');
                setShowDescriptionDialog(true);
              }}
              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-accent"
              aria-label="Edit description"
            >
              <Pencil className="h-3 w-3 text-muted-foreground hover:text-foreground" />
            </button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{chartDescription || 'No description'}</p>
        )}
      </div>

      {/* Right side: Actions */}
      <div className="flex items-center gap-2 ml-4">
        {/* Delete Button */}
        {onDelete && (
          <>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(true)}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Report</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete <strong>{chartName}</strong>? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    disabled={isDeleting}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={async (e) => {
                      e.preventDefault();
                      setIsDeleting(true);
                      try {
                        await onDelete();
                      } finally {
                        setIsDeleting(false);
                        setShowDeleteDialog(false);
                      }
                    }}
                  >
                    {isDeleting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      'Delete'
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
        {/* Save Button */}
        {onSave && (
          <Button
            variant={hasUnsavedChanges ? "default" : "outline"}
            onClick={onSave}
            disabled={!hasUnsavedChanges || isSaving}
            className={hasUnsavedChanges ? "bg-primary text-primary-foreground" : ""}
          >
            <Save className="h-4 w-4 mr-2" />
            Save
          </Button>
        )}
      </div>

      {/* Edit Description Dialog */}
      <Dialog open={showDescriptionDialog} onOpenChange={setShowDescriptionDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitleText>Edit Description</DialogTitleText>
          </DialogHeader>
          <DialogBody className="grid gap-4">
            <Input
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onDescriptionChange?.(editedDescription.trim());
                  setShowDescriptionDialog(false);
                } else if (e.key === 'Escape') {
                  setShowDescriptionDialog(false);
                }
              }}
              placeholder="Enter report description"
              autoFocus
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDescriptionDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => {
              onDescriptionChange?.(editedDescription.trim());
              setShowDescriptionDialog(false);
            }}>
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
